import { CurrentTestState, CurrentUserAssessmentState } from '@app/storage'
import { FC, useCallback, useEffect, useState } from 'react'
import { useSnapshot } from 'valtio'
import {
  checkValidityString,
  getResponseSingleItem,
  useDebouncing,
  useReactiveItemResponse,
  useResponseUpdate,
  useStateReset,
  useAssessmentItemResponseScoring,
} from '@app/helpers'
import { TextEditorComponent } from '@app/components/ui/editors/TextEditor'
import { InteractionType, TextFormatType } from '@app/types'

interface ExtendedTextInteractionProps {
  itemId: string
  responseidentifier: string
  expectedlength?: number
  expectedlengthdisplay?: 'percentage' | 'count'
  base?: number
  stringidentifier?: string
  patternmask?: string
  placeholdertext?: string
  format?: TextFormatType
  maxstrings?: number
  minstrings?: number
  expectedLines: number
  children?: JSX.Element
  accessibilityAttr?: { [key: string]: string }
}

interface ITextEditorValue {
  text: string
  xhtml: string
}

const getLinesCount = (response: string[]) => (response[0] ? response[0].split('\n').length - 1 : 0)

const getFormatText = (value: ITextEditorValue, format: TextFormatType) => {
  switch (format) {
    case TextFormatType.plain:
      return value.text
    case TextFormatType.preformatted:
      return `<pre>${value.text}</pre>`
    case TextFormatType.xhtml:
      return value.xhtml
    default:
      return value.xhtml
  }
}

const xhtmlToText = (xhtml: string) =>
  xhtml
    .replace(/( |<([^>]+)>)/gi, '')
    .replace('\n', ' ')
    .trim()

const transformResponse = (value: string) => [value]

const isHtmlResponse = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value)

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const plainTextToQuillHtml = (value: string) =>
  value
    .split('\n')
    .map(line => `<p>${line ? escapeHtml(line) : '<br>'}</p>`)
    .join('')

const getTextValueFromResponse = (value: string) => (isHtmlResponse(value) ? xhtmlToText(value) : value)

const getEditorValueFromResponse = (value: string) => {
  if (!value || isHtmlResponse(value)) {
    return value
  }

  return plainTextToQuillHtml(value)
}

export const ExtendedTextInteraction: FC<ExtendedTextInteractionProps> = props => {
  const {
    responseidentifier: responseIdentifier,
    placeholdertext: placeholder,
    expectedlengthdisplay: expectedLengthDisplay,
    maxstrings: maxStrings = '0',
    minstrings: minStrings = '0',
    patternmask: patternMask,
    expectedlength: expectedLength,
    format,
    children,
    accessibilityAttr,
  } = props
  const { currentItemResponse, currentTestPart } = useSnapshot(CurrentTestState)
  const { subject } = useSnapshot(CurrentUserAssessmentState)
  const itemResponse = useReactiveItemResponse(responseIdentifier)
  const { correctResponse, groupMode } = useAssessmentItemResponseScoring(
    currentItemResponse?.itemId,
    responseIdentifier,
  )
  const isGroupWritingReview = groupMode && subject?.toLowerCase() === 'writing'

  const [text, setText] = useState('')
  const [xhtml, setXhtml] = useState('')
  const [lines, setLines] = useState<number>(null)
  const [isValid, setValid] = useState(true)
  const debouncedValue = useDebouncing(getFormatText({ text, xhtml }, format))

  useResponseUpdate(
    currentItemResponse?.id,
    responseIdentifier,
    debouncedValue?.trim() ?? '',
    InteractionType.extendedText,
    transformResponse,
    isValid,
  )

  useEffect(() => {
    if (lines >= Number(minStrings) || lines <= Number(maxStrings)) {
      setValid(checkValidityString(patternMask, xhtmlToText(xhtml)))
    }
  }, [lines, xhtml, minStrings, maxStrings, patternMask])

  useEffect(() => {
    // In group review mode (including preview), show the correct response
    if (groupMode && correctResponse) {
      const correctAnswer = correctResponse?.[0] || ''
      setText(getTextValueFromResponse(correctAnswer))
      setXhtml(getEditorValueFromResponse(correctAnswer))
      setLines(getLinesCount([correctAnswer]))
      return
    }

    if (!itemResponse) return
    const responseValue = getResponseSingleItem(itemResponse)
    setText(getTextValueFromResponse(responseValue))
    setXhtml(getEditorValueFromResponse(responseValue))
    setLines(getLinesCount(itemResponse))
  }, [itemResponse, groupMode, correctResponse])

  const getEditorValue = useCallback(
    (response: string[]) => getEditorValueFromResponse(getResponseSingleItem(response)),
    [],
  )

  useStateReset(currentItemResponse?.id, responseIdentifier, setXhtml, getEditorValue)
  useStateReset(currentItemResponse?.id, responseIdentifier, setLines, getLinesCount)

  const changeHandler = (text: string, xhtml: string, countLines: number) => {
    setText(text)
    setXhtml(xhtml)
    setLines(countLines)
  }

  if (isGroupWritingReview) {
    return <>{children}</>
  }

  return (
    <>
      {children}
      {currentItemResponse.id && (
        <TextEditorComponent
          key={`${currentItemResponse.id}-${format}`}
          value={xhtml}
          {...accessibilityAttr}
          changeHandler={changeHandler}
          placeholder={placeholder}
          isDisabled={currentTestPart.isFinished || groupMode}
          expectedLength={expectedLength}
          expectedLengthDisplay={expectedLengthDisplay}
          format={format}
        />
      )}
    </>
  )
}
